// src/services/tenantSlugService.js
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  deleteDoc,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { generateSlug, validateSlug, generateUniqueSlug } from '../utils/slugUtils';

class TenantSlugService {
  constructor() {
    this.slugsCollection = 'tenantSlugs';
    this.tenantsCollection = 'tenants';
  }

  /**
   * Check if a slug is available
   * @param {string} slug - Slug to check
   * @returns {Promise<boolean>} True if available
   */
  async isSlugAvailable(slug) {
    if (!db) return false;
    
    try {
      const slugDoc = await getDoc(doc(db, this.slugsCollection, slug));
      return !slugDoc.exists();
    } catch (error) {
      console.error('Error checking slug availability:', error);
      return false;
    }
  }

  /**
   * Reserve a slug for a tenant
   * @param {string} slug - Slug to reserve
   * @param {string} tenantId - Tenant ID
   * @param {Object} tenantData - Tenant metadata
   * @returns {Promise<void>}
   */
  async reserveSlug(slug, tenantId, tenantData = {}) {
    if (!db) throw new Error('Firebase not available');

    const validation = validateSlug(slug);
    if (!validation.isValid) {
      throw new Error(validation.error);
    }

    const isAvailable = await this.isSlugAvailable(slug);
    if (!isAvailable) {
      throw new Error('Slug is already taken');
    }

    try {
      await setDoc(doc(db, this.slugsCollection, slug), {
        tenantId,
        active: tenantData.active !== false,
        name: tenantData.name || '',
        logoUrl: tenantData.logoUrl || '',
        timezone: tenantData.timezone || 'UTC',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error reserving slug:', error);
      throw error;
    }
  }

  /**
   * Update slug mapping when tenant data changes
   * @param {string} slug - Current slug
   * @param {string} tenantId - Tenant ID
   * @param {Object} tenantData - Updated tenant data
   * @returns {Promise<void>}
   */
  async updateSlugMapping(slug, tenantId, tenantData) {
    if (!db) throw new Error('Firebase not available');

    try {
      const slugDocRef = doc(db, this.slugsCollection, slug);
      await setDoc(slugDocRef, {
        tenantId,
        active: tenantData.active !== false,
        name: tenantData.name || '',
        logoUrl: tenantData.logoUrl || '',
        timezone: tenantData.timezone || 'UTC',
        updatedAt: new Date().toISOString()
      }, { merge: true });
    } catch (error) {
      console.error('Error updating slug mapping:', error);
      throw error;
    }
  }

  /**
   * Resolve tenant ID from slug
   * @param {string} slug - Slug to resolve
   * @returns {Promise<Object|null>} Tenant data or null if not found
   */
  async resolveTenant(slug) {
    if (!db || !slug) return null;

    try {
      const slugDoc = await getDoc(doc(db, this.slugsCollection, slug));
      
      if (!slugDoc.exists()) {
        return null;
      }

      const slugData = slugDoc.data();
      
      // Check if tenant is active
      if (!slugData.active) {
        return null;
      }

      return {
        tenantId: slugData.tenantId,
        name: slugData.name,
        logoUrl: slugData.logoUrl,
        timezone: slugData.timezone,
        active: slugData.active
      };
    } catch (error) {
      console.error('Error resolving tenant from slug:', error);
      return null;
    }
  }

  /**
   * Generate and reserve a unique slug for a tenant
   * @param {string} clinicName - Clinic name to generate slug from
   * @param {string} tenantId - Tenant ID
   * @param {Object} tenantData - Tenant metadata
   * @returns {Promise<string>} Reserved slug
   */
  async generateAndReserveSlug(clinicName, tenantId, tenantData = {}) {
    const baseSlug = generateSlug(clinicName);
    
    if (!baseSlug) {
      throw new Error('Unable to generate slug from clinic name');
    }

    const uniqueSlug = await generateUniqueSlug(
      baseSlug, 
      (slug) => this.isSlugAvailable(slug)
    );

    await this.reserveSlug(uniqueSlug, tenantId, tenantData);
    return uniqueSlug;
  }

  /**
   * Update tenant slug when clinic name changes
   * @param {string} tenantId - Tenant ID
   * @param {string} newClinicName - New clinic name
   * @param {string} currentSlug - Current slug (optional)
   * @param {Object} tenantData - Tenant metadata
   * @returns {Promise<string>} New slug
   */
  async updateTenantSlug(tenantId, newClinicName, currentSlug = null, tenantData = {}) {
    const newBaseSlug = generateSlug(newClinicName);
    
    // If the new slug would be the same as current, just update mapping
    if (currentSlug === newBaseSlug) {
      await this.updateSlugMapping(currentSlug, tenantId, tenantData);
      return currentSlug;
    }

    // Generate new unique slug
    const newSlug = await generateUniqueSlug(
      newBaseSlug,
      (slug) => this.isSlugAvailable(slug)
    );

    // Reserve new slug
    await this.reserveSlug(newSlug, tenantId, tenantData);

    // Remove old slug if it exists
    if (currentSlug) {
      try {
        await deleteDoc(doc(db, this.slugsCollection, currentSlug));
      } catch (error) {
        console.warn('Error removing old slug:', error);
      }
    }

    return newSlug;
  }

  /**
   * Get the primary slug for a tenant
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<string|null>} Slug or null if not found
   */
  async getSlugByTenantId(tenantId) {
    if (!db) return null;

    try {
      const q = query(
        collection(db, this.slugsCollection),
        where('tenantId', '==', tenantId)
      );
      
      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) {
        return null;
      }
      
      // Return the first slug found
      return querySnapshot.docs[0].id;
    } catch (error) {
      console.error('Error getting slug by tenant ID:', error);
      return null;
    }
  }

  /**
   * Create or update a slug for a tenant
   * @param {string} slug - Slug to create/update
   * @param {Object} tenantData - Tenant data
   * @returns {Promise<void>}
   */
  async createOrUpdateSlug(slug, tenantData) {
    if (!db) throw new Error('Firebase not available');

    const validation = validateSlug(slug);
    if (!validation.isValid) {
      throw new Error(validation.error);
    }

    try {
      // Check if slug exists and belongs to different tenant
      const existingDoc = await getDoc(doc(db, this.slugsCollection, slug));
      if (existingDoc.exists() && existingDoc.data().tenantId !== tenantData.tenantId) {
        throw new Error('Slug is already taken by another tenant');
      }

      // Create/update the slug
      await setDoc(doc(db, this.slugsCollection, slug), {
        ...tenantData,
        createdAt: existingDoc.exists() ? existingDoc.data().createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error creating/updating slug:', error);
      throw error;
    }
  }

  /**
   * Get all slugs for a tenant (in case of multiple)
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<Array>} Array of slug data
   */
  async getTenantSlugs(tenantId) {
    if (!db) return [];

    try {
      const q = query(
        collection(db, this.slugsCollection),
        where('tenantId', '==', tenantId)
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        slug: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting tenant slugs:', error);
      return [];
    }
  }

  /**
   * Remove slug reservation
   * @param {string} slug - Slug to remove
   * @returns {Promise<void>}
   */
  async removeSlug(slug) {
    if (!db) throw new Error('Firebase not available');

    try {
      await deleteDoc(doc(db, this.slugsCollection, slug));
    } catch (error) {
      console.error('Error removing slug:', error);
      throw error;
    }
  }
}

export default new TenantSlugService();
